if (!customElements.get('product-configurator')) {
  customElements.define(
    'product-configurator',
    class ProductConfigurator extends HTMLElement {
      connectedCallback() {
        this.steps = this.querySelectorAll('.product-configurator__step');
        this.nextStepButtons = this.querySelectorAll('button[type="button"][name="next_step"]');
        this.prevStepButtons = this.querySelectorAll('button[type="button"][name="prev_step"]');
        this.variantSelects = this.querySelector('variant-selects');
        this.step2Products = this.querySelectorAll('[data-product-container]');
        this.selectedVariantScript = this.variantSelects?.querySelector('[data-selected-variant]');
        this.currentStepIndex = 1;
        
        this.initializeDropZones();
        this.initializeStepNavigation();

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.updateVariantData(event.data.html);
        });
      }

      disconnectedCallback() {
        if (this.unsubscribe) {
          this.unsubscribe();
        }
      }

      initializeStepNavigation() {
        // Add event listeners to all next/prev buttons
        this.nextStepButtons.forEach(button => {
          button.addEventListener('click', this.onNextStep.bind(this));
        });
        
        this.prevStepButtons.forEach(button => {
          button.addEventListener('click', this.onPrevStep.bind(this));
        });

        // Initialize the first step as active
        this.setActiveStep(1);
      }

      onNextStep() {
        // Handle variant data synchronization
        this.syncVariantData();
        
        // Navigate to next step
        if (this.currentStepIndex < this.steps.length) {
          this.setActiveStep(this.currentStepIndex + 1, true);
        }
      }

      onPrevStep() {
        if (this.currentStepIndex > 1) {
          this.setActiveStep(this.currentStepIndex - 1, true);
        }
      }

      setActiveStep(stepIndex, shouldScroll = false) {
        this.currentStepIndex = stepIndex;
        
        this.steps.forEach((step, index) => {
          const stepNumber = parseInt(step.dataset.stepIndex, 10);
          const isActive = stepNumber === stepIndex;
          
          if (isActive) {
            step.classList.add('active');
            step.removeAttribute('hidden');
          } else {
            step.classList.remove('active');
            step.setAttribute('hidden', '');
          }
        });

        // Scroll after DOM updates if requested
        if (shouldScroll) {
          this.scrollToTop();
        }
      }

      scrollToTop() {
        // Use requestAnimationFrame to ensure DOM updates are complete
        requestAnimationFrame(() => {
          // Add a small delay to ensure layout has settled after step changes
          setTimeout(() => {
            // Prevent header reveal during scroll
            this.dispatchEvent(new CustomEvent('preventHeaderReveal'));
            
            // Get the configurator's position relative to the document
            const configuratorRect = this.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const configuratorTop = configuratorRect.top + scrollTop;
            
            // Get header height from CSS variable, fallback to 0
            const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
            
            // Calculate target scroll position (configurator top minus header height)
            const targetScrollTop = configuratorTop - headerHeight - 20; // 20px extra padding
            
            // Smooth scroll to the calculated position
            window.scrollTo({
              top: Math.max(0, targetScrollTop), // Ensure we don't scroll above the page
              behavior: 'smooth'
            });
          }, 50);
        });
      }

      initializeDropZones() {
        const dropZoneTemplate = document.querySelector('#drop-zone-template');
        if (!dropZoneTemplate) return;

        this.querySelectorAll('input[type="file"]').forEach((fileInput) => {
          const dropZone = dropZoneTemplate.content.cloneNode(true).querySelector('.drop-zone');
          const prompt = dropZone.querySelector('.drop-zone__prompt');
          const fileList = dropZone.querySelector('.drop-zone__files');

          fileInput.style.display = 'none';
          fileInput.parentNode.insertBefore(dropZone, fileInput);

          dropZone.addEventListener('click', () => fileInput.click());

          dropZone.addEventListener('dragover', (event) => {
            event.preventDefault();
            dropZone.classList.add('drop-zone--over');
          });

          dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drop-zone--over'));

          dropZone.addEventListener('drop', (event) => {
            event.preventDefault();
            dropZone.classList.remove('drop-zone--over');
            fileInput.files = event.dataTransfer.files;
            this.updateFileList(fileInput, prompt, fileList);
          });

          fileInput.addEventListener('change', () => this.updateFileList(fileInput, prompt, fileList));
        });
      }

      updateFileList(fileInput, prompt, fileList) {
        if (fileInput.files.length > 0) {
          prompt.style.display = 'none';
          fileList.innerHTML = Array.from(fileInput.files)
            .map((file) => `<div class="drop-zone__file-name">${file.name}</div>`)
            .join('');
        } else {
          prompt.style.display = 'block';
          fileList.innerHTML = '';
        }
      }

      updateVariantData(html) {
        const newVariantData = html.querySelector('variant-selects [data-selected-variant]');
        if (newVariantData && this.selectedVariantScript) {
          this.selectedVariantScript.textContent = newVariantData.textContent;
        }
      }

      onNextStep() {
        // Handle variant data synchronization
        this.syncVariantData();
        
        // Navigate to next step
        if (this.currentStepIndex < this.steps.length) {
          this.setActiveStep(this.currentStepIndex + 1);
          this.scrollToTop();
        }
      }

      syncVariantData() {
        if (!this.selectedVariantScript) return;

        try {
          const mainVariantData = JSON.parse(this.selectedVariantScript.textContent);
          if (!mainVariantData?.options) return;

          const { options: mainVariantOptions } = mainVariantData;

          this.step2Products.forEach((productContainer) => {
            const variantsJsonEl = productContainer.querySelector('.product-variants-json');
            const targetInput = productContainer.querySelector('input[name="product_variant_id"][data-update-variant]');
            if (!variantsJsonEl || !targetInput) return;

            const variants = JSON.parse(variantsJsonEl.textContent);
            if (!variants) return;

            const matchingVariant = variants.find(
              (variant) =>
                variant.options?.length === mainVariantOptions.length &&
                variant.options.every((option, index) => option === mainVariantOptions[index])
            );

            targetInput.value = matchingVariant?.id || '';
          });
        } catch (e) {
          console.error('Error parsing variant data in product configurator:', e);
        }
      }
    }
  );
}
